import { capitalize, Chip, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useGetByDevice } from '@onesource/components';
import { LessonProgressStatus, type TContentBlockBase } from '@onesource/schemas';
import {
  useCompleteBlock,
  useGetMembersInstructors,
  useGetSpace,
  useGetVideoWatchCounts,
  useUpdateProgress,
} from '@onesource/services';
import { formatDurationLabel } from '@onesource/utils';
import { useParams } from '@tanstack/react-router';
import Player from '@vimeo/player';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdAccessTime, MdCheckCircleOutline, MdClose, MdOutlineVisibility } from 'react-icons/md';

import { ContentCategoryBadge } from '#/features/community-hub/components/content-category-badge';

interface VideoBlockPlayerProps {
  block: TContentBlockBase;
  lessonId: string;
  /** When provided, a close button is shown (single-block page). Omit for the inline lesson view. */
  onClose?: () => void;
  /**
   * Compact card styling for the inline lesson template: tighter padding and a
   * rounded white card so the player sits neatly under the lesson layout
   * instead of spanning the full content width.
   */
  compact?: boolean;
  /**
   * Hero styling for the inline template's above-the-fold player: no white card
   * or padding, so the header line and the rounded player sit directly on the
   * page background (see the RYC® lessons page design).
   */
  hero?: boolean;
}

/**
 * Renders a single Vimeo video block with its progress tracking. Shared by the
 * single-block route page and the inline lesson template so the player and
 * tracking logic live in exactly one place.
 *
 * View tracking (IN_PROGRESS) is gated behind an IntersectionObserver so that,
 * on the inline template where many blocks share one page, each block records a
 * view only once when it first scrolls into the viewport — never all at once on
 * load. On a single-block page the block is visible immediately, so it behaves
 * like the previous on-mount tracking.
 */
export function VideoBlockPlayer({ block, lessonId, onClose, compact = false, hero = false }: VideoBlockPlayerProps) {
  const theme = useTheme();
  const { isMobile } = useGetByDevice();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasTrackedPlay, setHasTrackedPlay] = useState(false);

  const { data: instructorsData } = useGetMembersInstructors();
  const instructor = instructorsData?.items?.find(i => String(i.id) === block.instructorId);

  // The watch-count chip is an opt-in per-space setting (ch_spaces.show_watch_count,
  // toggled in the dashboard space dialog). Both usage sites render under
  // /community-hub/spaces/$id, so the top-level space id comes from the route.
  const { id: spaceId } = useParams({ strict: false });
  const { data: space } = useGetSpace(spaceId ?? '');
  const showWatchCount = space?.showWatchCount === true;

  const { data: watchCounts } = useGetVideoWatchCounts(lessonId, showWatchCount);
  const watchCount = watchCounts?.[block.id] ?? 0;

  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  const { mutate: completeBlock } = useCompleteBlock();

  // Track a "view" once when the block first scrolls into view.
  const viewTrackedRef = useRef(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && !viewTrackedRef.current) {
          viewTrackedRef.current = true;
          updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: block.id, logAction: 'VIDEO_VIEW' });
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [block.id, updateProgress]);

  const onVideoPlay = useCallback(() => {
    if (!hasTrackedPlay) {
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: block.id });
      setHasTrackedPlay(true);
    }
  }, [block.id, updateProgress, hasTrackedPlay]);

  const onVideoEnd = useCallback(() => {
    completeBlock(block.id);
  }, [block.id, completeBlock]);

  const handleMarkAsWatched = useCallback(() => {
    completeBlock(block.id);
  }, [block.id, completeBlock]);

  // Setup Vimeo player event listeners
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }
    const player = new Player(iframe);
    player.on('play', onVideoPlay);
    player.on('ended', onVideoEnd);
    return () => {
      player.off('play');
      player.off('ended');
    };
  }, [onVideoPlay, onVideoEnd]);

  const duration = block.vimeoDurationSeconds;

  // Hero sits on the page background with no card chrome; compact is the white
  // rounded card used further down the inline lesson page.
  const containerPaddingBottom = hero ? 0 : compact ? 2 : 4;
  const containerPaddingX = hero ? 0 : compact ? { xs: 2, md: 3 } : { xs: 2, md: 6 };

  // Meta line — category, duration, watch count, mark-as-watched and (on the
  // single-block page) the close button. Shared by both header layouts below.
  const metaContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
      {block.contentCategory && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ContentCategoryBadge size="medium" color="primary" category={block.contentCategory} />
          <Typography variant="caption" sx={{ color: theme.palette.grey[600], fontSize: '9px' }}>
            {capitalize(block.contentCategory.toLowerCase())}
          </Typography>
        </Box>
      )}
      {duration && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MdAccessTime size={15} color={theme.palette.primary.main} />
          <Typography variant="caption" sx={{ color: theme.palette.grey[600], fontSize: '9px', whiteSpace: 'nowrap' }}>
            {formatDurationLabel(block.vimeoDurationSeconds)}
          </Typography>
        </Box>
      )}

      {showWatchCount && watchCount > 0 && (
        <Chip
          label={`Watched ${watchCount}x`}
          icon={<MdOutlineVisibility size={15} color={theme.palette.primary.main} />}
          color="primary"
          size="small"
          sx={{ height: 18, fontSize: 9 }}
        />
      )}

      {/* Mark as Watched Button */}
      {block.isPublicViewMarkAndWatched && (
        <Button
          variant="text"
          size="small"
          disableRipple
          startIcon={<MdCheckCircleOutline size={12} color={theme.palette.primary.main} />}
          onClick={handleMarkAsWatched}
          sx={{
            color: 'text.primary',
            fontSize: 9,
            minWidth: 'auto',
            '& .MuiButton-icon': { mr: 0.5 },
          }}
        >
          {isMobile ? 'Watched' : 'Mark as Watched'}
        </Button>
      )}

      {/* Close button — only on the single-block page */}
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            width: 24,
            height: 24,
            color: 'text.primary',
            fontSize: 16,
            backgroundColor: theme.palette.grey[100],
          }}
        >
          <MdClose />
        </IconButton>
      )}
    </Box>
  );

  const instructorName = instructor ? `${instructor.firstName} ${instructor.lastName}` : '';

  return (
    <Box
      ref={containerRef}
      sx={{
        pt: hero ? 0 : 2,
        pb: containerPaddingBottom,
        px: containerPaddingX,
        background: hero ? 'transparent' : 'white',
        ...(!hero && compact && { borderRadius: '15px', overflow: 'hidden' }),
      }}
    >
      {block.vimeoId ? (
        <Box>
          {/* Video Header Bar. The above-the-fold hero player stacks the title
              on its own line on mobile, with the instructor and the meta line
              beneath it (see the RYC® lessons page design). */}
          {hero && isMobile ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 14 }}>
                {block.title || 'Video'}
              </Typography>
              <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 12, color: theme.palette.grey[600] }}>
                  {instructorName}
                </Typography>
                {metaContent}
              </Box>
            </Box>
          ) : (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: 9, md: 16 } }}>
                  {block.title || 'Video'}
                </Typography>

                {instructor ? <Divider orientation="vertical" flexItem /> : null}

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 400, fontSize: { xs: 9, md: 16 }, color: theme.palette.grey[600] }}
                >
                  {instructorName}
                </Typography>
              </Box>

              {metaContent}
            </Box>
          )}

          {/* Video Player */}
          <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe
              ref={iframeRef}
              src={`https://player.vimeo.com/video/${block.vimeoId}?autoplay=0`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '13px',
              }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={block.title || 'Video'}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: 400,
            bgcolor: 'grey.200',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: 900,
          }}
        >
          <Typography color="text.secondary">Video not available</Typography>
        </Box>
      )}
    </Box>
  );
}
